import React, { useCallback } from "react";

import { useBreakpointV2 } from "@tm/ui-widgets";

import { ImageComponent, ReactPortalComp } from "../../../../../components";
import CommonModalDweb from "../../../../../components/CommonModalDweb/CommonModalDweb";
import FormattedText, {
  getPlainText,
} from "../../../../../components/FormattedText/FormattedText";
import cancelGreyBg from "../../../../../icons/cancelGreyBg.svg";
import type { ClaimEligibilityCta } from "../../../types";
import {
  CloseButton,
  CriteriaBody,
  CriteriaDesc,
  CriteriaItem,
  CriteriaList,
  CriteriaName,
  CriteriaNumber,
  Overlay,
  PrimaryCta,
  SheetHeader,
  SheetPanel,
  SheetTitle,
} from "./EligibilityModal.style";

type EligibilityData = NonNullable<ClaimEligibilityCta["action"]>["data"];

export interface EligibilityModalProps {
  data?: EligibilityData;
  onClose: () => void;
}

export function EligibilityModal({ data, onClose }: EligibilityModalProps) {
  const device = useBreakpointV2();
  const title = getPlainText(data?.title).trim();
  const criteria = data?.criteria ?? [];
  const primaryCta = data?.primaryCta;

  const handleOverlayClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (e.target === e.currentTarget) onClose();
    },
    [onClose]
  );

  if (!criteria.length) return null;

  const body = (
    <>
      <CriteriaList>
        {criteria.map((item, idx) => (
          <CriteriaItem key={item.id ?? idx}>
            <CriteriaNumber>{idx + 1}.</CriteriaNumber>
            <CriteriaBody>
              {getPlainText(item.name).trim() ? (
                <CriteriaName>
                  <FormattedText value={item.name} />
                </CriteriaName>
              ) : null}
              {getPlainText(item.description).trim() ? (
                <CriteriaDesc>
                  <FormattedText value={item.description} />
                </CriteriaDesc>
              ) : null}
            </CriteriaBody>
          </CriteriaItem>
        ))}
      </CriteriaList>
      {getPlainText(primaryCta).trim() ? (
        <PrimaryCta type="button" onClick={onClose}>
          <FormattedText value={primaryCta} />
        </PrimaryCta>
      ) : null}
    </>
  );

  if (device === "sm") {
    return (
      <ReactPortalComp>
        <Overlay onClick={handleOverlayClick}>
          <SheetPanel
            role="dialog"
            aria-modal="true"
            aria-label={title}
            $maxHeight="75vh"
          >
            <SheetHeader>
              <SheetTitle>
                <FormattedText value={data?.title} />
              </SheetTitle>
              <CloseButton onClick={onClose} aria-label="Close">
                <ImageComponent
                  src={cancelGreyBg.src}
                  width="28px"
                  height="28px"
                  alt="Close"
                />
              </CloseButton>
            </SheetHeader>
            {body}
          </SheetPanel>
        </Overlay>
      </ReactPortalComp>
    );
  }

  return (
    <CommonModalDweb
      customTitle={title}
      handleClick={onClose}
      customWidth="420px"
      customHeight="auto"
    >
      {body}
    </CommonModalDweb>
  );
}
